'use client';

// ── Leverage · "Example · Leverage 5:1" ──────────────────────────────────────
// Figma: Moldura · Exemplo · alavancagem — desktop 12071:59935 (792×300),
// mobile 12471:8445 (366×656). The three blocks sit in a row on desktop and
// stack on mobile; the proportion bar moves its labels below the track on mobile.

import { cn } from '@/lib/utils';
import { ExampleLabel, FigureFrame } from '../primitives';

function Block({
  label,
  value,
  tone = 'gray',
}: {
  label: string;
  value: string;
  tone?: 'gray' | 'green';
}) {
  const green = tone === 'green';
  return (
    <div
      data-ui="figure.leverageExample.block"
      className={cn(
        'flex w-full flex-col gap-[0.5rem] rounded-[0.25rem] p-[2rem] font-geist lg:min-w-0 lg:w-auto lg:flex-1 lg:self-stretch',
        green ? 'bg-[#228e71]' : 'bg-[#f2f4f3]',
      )}
    >
      <p className={cn('mb-0 whitespace-nowrap text-[2rem] leading-[3rem]', green ? 'text-white/72' : 'text-black/64')}>
        {label}
      </p>
      <p className={cn('mb-0 whitespace-nowrap text-[3rem] font-medium leading-[4rem]', green ? 'text-white' : 'text-black')}>
        {value}
      </p>
    </div>
  );
}

function Operator({ children }: { children: string }) {
  return (
    <span className="flex h-[3.5rem] shrink-0 items-center justify-center font-geist text-[2.5rem] leading-[3.5rem] text-black/32 lg:w-[2rem]">
      {children}
    </span>
  );
}

export default function LeverageExample() {
  return (
    <FigureFrame data-ui="figure.leverageExample" padding="p-[2rem] lg:p-[3rem]" className="gap-[2.5rem]">
      <ExampleLabel>EXAMPLE · LEVERAGE 5:1</ExampleLabel>

      {/* Position size ÷ Leverage = Margin used */}
      <div
        data-ui="figure.leverageExample.calc"
        className="flex w-full flex-col items-center gap-[1.5rem] lg:flex-row lg:items-center"
      >
        <Block label="Position size" value="100,000 USD" />
        <Operator>÷</Operator>
        <Block label="Leverage" value="5" />
        <Operator>=</Operator>
        <Block label="Margin used" value="20,000 USD" tone="green" />
      </div>

      {/* Proportion bar */}
      <div data-ui="figure.leverageExample.bar" className="relative h-[11rem] w-full lg:h-[3.5rem]">
        <div className="absolute left-0 top-0 h-[3.5rem] w-full rounded-[0.25rem] bg-black/[0.06]" />
        <div
          data-ui="figure.leverageExample.fill"
          className="absolute left-0 top-0 h-[3.5rem] w-[8.3rem] rounded-[0.25rem] bg-[#228e71] lg:w-[17.375rem]"
        />
        <p className="absolute left-[1.25rem] top-[4.5rem] mb-0 whitespace-nowrap font-geist text-[2rem] font-medium leading-[3rem] text-[#228e71] lg:left-[18.375rem] lg:top-[0.25rem] lg:text-[#196551]">
          Margin used 20,000 USD
        </p>
        <p className="absolute left-[1.25rem] top-[8rem] mb-0 whitespace-nowrap font-geist text-[2rem] leading-[3rem] text-black/64 lg:left-[62.5rem] lg:top-[0.25rem]">
          Position size 100,000 USD
        </p>
      </div>

      <p data-ui="figure.leverageExample.note" className="mb-0 font-geist text-[2rem] leading-[3rem] text-black/64 lg:w-[73.25rem]">
        Leverage differs by asset class (e.g. FX, indices &amp; commodities, crypto). The exact values are on the product
        page.
      </p>
    </FigureFrame>
  );
}
